// ✅ FILE 1: SocketContext.jsx
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
} from "react";
import { io } from "socket.io-client";

const PriceContext = createContext(null);

export const PriceProvider = ({ children }) => {
  const [prices, setPrices] = useState([]);
  const socketRef = useRef(null);
  const candleMap = useRef(new Map());
  const elapsedSeconds = useRef(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    const socket = io(
      process.env.REACT_APP_API_BASE_URL || "http://localhost:3003",
      {
        transports: ["websocket"],
        reconnectionAttempts: 5,
      }
    );

    socketRef.current = socket;

    socket.on("connect", () => {
      console.log("✅ Socket connected");
    });

    socket.on("priceUpdate", (data) => {
      console.log("📦 Received priceUpdate:", data.length, "coins");

      // Reset candleMap with new incoming data
      candleMap.current = new Map();
      data.forEach((coin) => {
        candleMap.current.set(coin.coinId, coin);
      });

      // Reset simulation
      elapsedSeconds.current = 0;
      clearInterval(intervalRef.current);
      intervalRef.current = setInterval(simulatePrices, 1000);
    });

    socket.on("disconnect", () => console.log("❌ Socket disconnected"));
    socket.on("connect_error", (err) => console.error("❗ Socket error:", err));

    return () => {
      socket.disconnect();
      clearInterval(intervalRef.current);
    };
  }, []);

  const simulatePrices = () => {
    const t = elapsedSeconds.current;
    const maxSeconds = 300;
    const simulated = [];

    candleMap.current.forEach((candle, coinId) => {
      const { chartPoint, ...rest } = candle;
      const { open, close, high, low, time } = chartPoint;

      const price = parseFloat(
        (open + ((close - open) * t) / maxSeconds).toFixed(6)
      );
      const simulatedHigh = Math.max(price, high);
      const simulatedLow = Math.min(price, low);

      simulated.push({
        ...rest,
        coinId,
        price,
        percentChange: candle.percentChange,
        color: candle.color,
        chartPoint: {
          time: time + t,
          open,
          high: simulatedHigh,
          low: simulatedLow,
          close: price,
        },
      });
    });

    setPrices(simulated);
    elapsedSeconds.current++;

    if (elapsedSeconds.current > maxSeconds) {
      clearInterval(intervalRef.current);
    }
  };

  return (
    <PriceContext.Provider value={prices}>{children}</PriceContext.Provider>
  );
};

export const usePrices = () => {
  const context = useContext(PriceContext);
  if (context === null) {
    throw new Error("usePrices must be used within a PriceProvider");
  }
  return context;
};
